/*
 * PBM registration & verification, theme side (package B, branch registration-2026-09-27).
 *
 * Plain JavaScript, no jQuery and no theme framework: the theme's app.js/utilities.js load
 * late (lazyload2), so everything here runs from a normal `defer` script.
 *
 * Contracts (HANDOFF_for_Opus_registration_build.md):
 *   §2.1 browser -> service: POST text/plain JSON to SERVICE_URL?r=<route>; GET card JSON.
 *   §2.2 routes used here: org (Screen 2 beacon), request_director, w9, card.
 *   §2.6 Screen 2 posts the customer_address form, then the org beacon, then goes to the
 *        return path or /pages/register. Cookies: pbm_signup, pbm_reg, pbm_dup.
 *
 * Buyer-facing words are rendered by Liquid (sections/pbm-register.liquid,
 * snippets/pbm-cart-lines.liquid). This file only shows or hides them and fills in names
 * and dates, so no buyer copy lives here.
 */
(function () {
  'use strict';

  var PBM = (window.PBMReg = window.PBMReg || {});
  if (PBM.loaded) return;
  PBM.loaded = true;

  var DAY = 86400;
  var DUP_FRESH_MS = 60 * 1000;
  var DRAFT_KEY = 'pbm_reg_draft';
  var ORG_TYPES = ['ems', 'hospital', 'provider'];

  /* OPP-12: Government, dental, and clinic are organization tiles only while sales-first is on. */
  function orgTypes() {
    var types = ORG_TYPES.slice();
    if (document.querySelector('[data-pbm-salesfirst="on"]')) {
      types.push('government');
      types.push('dental');
      types.push('clinic');
    }
    return types;
  }

  /* OPP-12 §10.3: unpublished theme + pbm_service_is_test off → no beacon, card, sign or upload call. */
  function serviceCallsAllowed() {
    var role = (window.Shopify && Shopify.theme && Shopify.theme.role) || '';
    if (role === 'main') return true;
    var marked = document.querySelector('[data-pbm-service-is-test="on"]');
    if (marked) return true;
    try { console.warn('PBM: service calls are off on this unpublished theme until pbm_service_is_test is on.'); } catch (e) {}
    return false;
  }

  function salesfirstPayload(root, payload) {
    if (!root || root.getAttribute('data-pbm-salesfirst') !== 'on') return payload;
    payload.salesfirst = true;
    payload.theme_role = (window.Shopify && Shopify.theme && Shopify.theme.role) || '';
    payload.service_is_test = root.getAttribute('data-pbm-service-is-test') === 'on';
    return payload;
  }

  function rememberTier(root, res) {
    if (!root || !res || !res.tier || root.getAttribute('data-pbm-salesfirst') !== 'on') return;
    try {
      sessionStorage.setItem('pbm_tier_' + root.getAttribute('data-pbm-customer'), JSON.stringify({
        tier: res.tier, org_type: res.org_type, rule_version: res.rule_version
      }));
    } catch (e) { /* the card still reads the metafield once the desk writes it */ }
  }

  /* fix12i-f (GL-F-41): the organization type this customer saved on the organization form in this browser
     (cookie pbm_org_type = "<customer id>:<type>"), until the store tags the account. Another customer's value is ignored. */
  /* fix12i-g (GL-F-41): the tax step's rule for an untagged account (EMS, hospital; government while sales-first is on). */
  function taxForType(root, tt) {
    return tt === 'ems' || tt === 'hospital' || (tt === 'government' && root.getAttribute('data-pbm-salesfirst') === 'on');
  }

  /* fix12i-g (F-45): sessionStorage pbm_org_saved_<customer id> = the time the organization form was saved in this tab. */
  var ORG_SAVED = 'pbm_org_saved_', POST_SAVE_MS = 20000;
  function orgSavedAt(cid) {
    var at = 0;
    try { at = +sessionStorage.getItem(ORG_SAVED + cid) || 0; } catch (e) { at = 0; }
    var age = Date.now() - at;
    return at && age >= 0 && age < POST_SAVE_MS ? at : 0;
  }

  /* fix12i-g (F-44): the saved type in the Organization row's summary line ("Medical Clinic, <Company>, <City> <ST>"), with the
     type's own tile name (data-pbm-type-names, from the same strings as the organization form). The store's type:* tag is
     written by the Liquid; until then the service's saved type (card org_type) or this browser's saved type. No label added. */
  function showTypeName(card, type) {
    var wrap = card && card.querySelector('[data-pbm-type-names]');
    var span = wrap && wrap.querySelector('[data-pbm-type-name]');
    if (!span || span.getAttribute('data-pbm-type-fixed') === '1') return;
    var names = {};
    try { names = JSON.parse(wrap.getAttribute('data-pbm-type-names') || '{}'); } catch (e) { names = {}; }
    var name = names[type];
    if (!name) return;
    var line = span.parentNode, rest = '';
    each(line.childNodes, function (n) { if (n !== span) rest += n.textContent || ''; });
    span.textContent = name + (rest.trim() ? ', ' : '');
    line.hidden = false;
  }

  function savedOrgType(root) {
    var v = getCookie('pbm_org_type') || '', cid = root.getAttribute('data-pbm-customer') || '';
    var i = v.indexOf(':');
    if (!cid || i < 1 || v.slice(0, i) !== cid) return '';
    var t = v.slice(i + 1);
    return orgTypes().indexOf(t) > -1 ? t : '';
  }

  /* fix12i-f (GL-F-42): the organization form keeps what was typed in this tab (sessionStorage pbm_org_draft_<customer id>)
     until it is saved, so a forward from /account back to the form does not blank it. Text fields, the state select and the
     type radios only: never hidden, file, password or token fields. Cleared on a successful save, on Log out, and for any
     other customer. Restored only into empty fields, so a value the store already has is never replaced. */
  var DRAFT_ORG = 'pbm_org_draft_';
  function dropOrgDrafts(keepCid) {
    try {
      for (var i = sessionStorage.length - 1; i >= 0; i--) {
        var k = sessionStorage.key(i);
        if (k && k.indexOf(DRAFT_ORG) === 0 && (!keepCid || k !== DRAFT_ORG + keepCid)) sessionStorage.removeItem(k);
      }
    } catch (e) { /* storage blocked */ }
  }
  function draftable(el) {
    if (!el || !el.name || el.disabled) return false;
    var ty = String(el.type || '').toLowerCase();
    if (['hidden', 'file', 'password', 'submit', 'button', 'checkbox', 'image', 'reset'].indexOf(ty) > -1) return false;
    if (/token|secret|password|authenticity|_method|form_type|utf8|recaptcha|captcha/i.test(el.name)) return false;
    return el.tagName === 'SELECT' || el.tagName === 'TEXTAREA' || ty === 'radio' || ty === 'text' || ty === 'tel' || ty === 'email' || ty === '';
  }

  /* ------------------------------------------------------------------ helpers */

  function noop() {}

  function setCookie(name, value, maxAgeSeconds) {
    var c = name + '=' + encodeURIComponent(String(value)) + '; path=/; SameSite=Lax';
    if (typeof maxAgeSeconds === 'number') c += '; max-age=' + maxAgeSeconds;
    if (location.protocol === 'https:') c += '; Secure';
    document.cookie = c;
  }

  function getCookie(name) {
    var parts = document.cookie ? document.cookie.split('; ') : [];
    for (var i = 0; i < parts.length; i++) {
      var eq = parts[i].indexOf('=');
      if (eq > 0 && parts[i].slice(0, eq) === name) {
        try { return decodeURIComponent(parts[i].slice(eq + 1)); } catch (e) { return ''; }
      }
    }
    return '';
  }

  function delCookie(name) { setCookie(name, '', 0); }

  function query() {
    if (PBM.query) return PBM.query;
    var out = {};
    try {
      new URLSearchParams(location.search).forEach(function (v, k) { out[k] = v; });
    } catch (e) { /* old browser: no query support needed */ }
    PBM.query = out;
    return out;
  }

  /* Only same-site relative paths, never another host. 'cart' is the door-3 shorthand. */
  function safeReturn(v) {
    if (!v) return '';
    if (v === 'cart') return '/cart';
    if (v.charAt(0) !== '/' || v.charAt(1) === '/' || v.charAt(1) === '\\') return '';
    if (/[\u0000-\u001f]/.test(v)) return '';
    return v;
  }

  /* Google's Apps Script front end reserves the query names c and sid: a request carrying either
     gets HTTP 400 before the service runs (10/12). They are never sent; the customer id is cid. */
  function serviceUrl(base, route, params) {
    if (!base) return '';
    if (params && (Object.prototype.hasOwnProperty.call(params, 'c') || Object.prototype.hasOwnProperty.call(params, 'sid'))) return '';
    var u;
    try { u = new URL(base); } catch (e) { return ''; }
    if (u.protocol !== 'https:') return '';
    u.searchParams.set('r', route);
    if (params) {
      Object.keys(params).forEach(function (k) {
        if (params[k] !== undefined && params[k] !== null) u.searchParams.set(k, params[k]);
      });
    }
    return u.toString();
  }

  function withTimeout(ms) {
    var ctrl = typeof AbortController === 'function' ? new AbortController() : null;
    var timer = ctrl ? setTimeout(function () { ctrl.abort(); }, ms) : null;
    return {
      signal: ctrl ? ctrl.signal : undefined,
      done: function () { if (timer) clearTimeout(timer); }
    };
  }

  /* §2.1: text/plain avoids the CORS preflight Apps Script cannot answer. */
  function postService(url, payload, opts) {
    opts = opts || {};
    if (!serviceCallsAllowed()) return Promise.reject(new Error('test_guard'));
    if (!url) return Promise.reject(new Error('no_service'));
    var t = withTimeout(opts.timeout || 8000);
    return fetch(url, {
      method: 'POST',
      headers: { 'Content-Type': 'text/plain;charset=utf-8' },
      body: JSON.stringify(payload),
      redirect: 'follow',
      credentials: 'omit',
      keepalive: !!opts.keepalive,
      signal: t.signal
    }).then(function (r) {
      t.done();
      /* fix12 (F-t-assess-02): with opts.strict, a body that is not JSON is an unknown outcome, never a success. */
      return r.json().catch(function () { return opts.strict ? { ok: false, error: 'unreadable', unknown: true } : { ok: r.ok }; });
    }, function (err) { t.done(); throw err; });
  }

  function getService(url, timeout) {
    if (!serviceCallsAllowed()) return Promise.reject(new Error('test_guard'));
    if (!url) return Promise.reject(new Error('no_service'));
    var t = withTimeout(timeout || 8000);
    return fetch(url, { method: 'GET', redirect: 'follow', credentials: 'omit', cache: 'no-store', signal: t.signal })
      .then(function (r) { t.done(); return r.json(); }, function (err) { t.done(); throw err; });
  }

  /* Dates: a bare YYYY-MM-DD is a calendar date (format in UTC so it never shifts a day);
     anything with a time is shown in Arizona time, like the rest of PBM's promises. */
  /* A date_time at UTC midnight (how the desk writes expiries) is a calendar date. */
  function calendarDate(s) {
    var m = /^(\d{4}-\d{2}-\d{2})(T00:00:00(\.0+)?(Z|[+-]00:?00))?$/.exec(String(s || ''));
    return m ? m[1] : '';
  }

  function formatDate(value) {
    if (!value) return '';
    var s = String(value);
    var cal = calendarDate(s);
    if (cal) s = cal;
    var dateOnly = !!cal;
    var d = new Date(dateOnly ? s + 'T00:00:00Z' : s);
    if (isNaN(d.getTime())) return s;
    try {
      return d.toLocaleDateString('en-US', {
        month: 'long', day: 'numeric', year: 'numeric',
        timeZone: dateOnly ? 'UTC' : 'America/Phoenix'
      });
    } catch (e) {
      return d.toDateString();
    }
  }

  /* An expiry is valid through its whole day (a bare date is taken as the end of that day in
     Arizona time, like the Liquid card). */
  function isPast(value) {
    if (!value) return false;
    var s = String(value);
    var cal = calendarDate(s);
    var t = cal ? new Date(cal + 'T23:59:59-07:00') : new Date(s);
    return !isNaN(t.getTime()) && t.getTime() < Date.now();
  }

  /* "Dr. [Name]" (fix12g F-54): the copy already says "Dr.", so drop a typed "Dr" / "Dr." prefix (any case, with or
     without a space after the period, repeated too): never "Dr. Dr.". "Drew" keeps its letters. */
  function directorDisplay(name) {
    var n = String(name || '').trim();
    for (var i = 0; i < 3; i++) {
      var m = n.replace(/^dr(\.\s*|\s+|$)/i, '');
      if (m === n) break;
      n = m.trim();
    }
    return n;
  }

  function each(list, fn) { Array.prototype.forEach.call(list || [], fn); }

  function show(el, on) { if (el) el.hidden = !on; }

  function busy(btn, on) {
    if (!btn) return;
    btn.disabled = !!on;
    btn.setAttribute('aria-busy', on ? 'true' : 'false');
    btn.classList.toggle('is-loading', !!on);
    /* OPP-01 #35: a labelled spinner ("Saving…") where the button asks for one. */
    var busyLabel = btn.getAttribute('data-label-busy');
    if (busyLabel) {
      if (on) {
        if (!btn.hasAttribute('data-label-idle')) btn.setAttribute('data-label-idle', btn.textContent);
        btn.textContent = busyLabel;
      } else if (btn.hasAttribute('data-label-idle')) {
        btn.textContent = btn.getAttribute('data-label-idle');
      }
    }
  }

  /* OPP-01 #7/#10: the email typed on Screen 1 or the login form, kept for this tab only. */
  function rememberEmail(v) {
    try { if (v) sessionStorage.setItem('pbm_email', String(v).trim()); } catch (e) { /* storage blocked */ }
  }
  function recalledEmail() {
    try { return sessionStorage.getItem('pbm_email') || ''; } catch (e) { return ''; }
  }

  /* Tokens live in a <script type="application/json" data-pbm-private> block, never in attributes. */
  function priv(root) {
    if (root.__pbmPriv) return root.__pbmPriv;
    var el = root.querySelector('script[data-pbm-private]');
    var data = {};
    try { data = el ? JSON.parse(el.textContent || '{}') : {}; } catch (e) { data = {}; }
    root.__pbmPriv = data;
    return data;
  }

  function checkedValue(inputs) {
    for (var i = 0; i < inputs.length; i++) if (inputs[i].checked) return inputs[i];
    return null;
  }

  /* ------------------------------------------------------------ Screen 1 */

  function initScreen1(root) {
    var screen = root.querySelector('[data-pbm-screen="create"]');
    if (!screen || screen.getAttribute('data-pbm-ready')) return;
    screen.setAttribute('data-pbm-ready', '1');

    var tiles = screen.querySelectorAll('input[name="pbm_buying_for"]');
    var form = screen.querySelector('form');
    var note = screen.querySelector('[data-pbm-note]');
    var hint = screen.querySelector('[data-pbm-org-hint]');
    var submit = screen.querySelector('[data-pbm-submit]');

    function apply(tile, remember) {
      if (!tile) return;
      var type = tile.value;
      var isOrg = orgTypes().indexOf(type) > -1;
      if (note) note.value = tile.getAttribute('data-note') || '';
      /* fix11p: every tile sends signup:<tile> with the create POST (Personal Use: signup:personal). The theme reads it
         on any device; only the desk writes type:*, so no desk rule keys on a self-declared tag. */
      var tag = screen.querySelector('[data-pbm-tag]');
      if (tag) {
        tag.value = type === 'personal' ? 'signup:personal' : (isOrg ? 'signup:' + type : '');
        tag.disabled = !tag.value;
      }
      show(hint, isOrg);
      /* The work-email hint describes the field only while it is shown (organization tiles). */
      var emailField = form ? form.querySelector('input[name="customer[email]"]') : null;
      if (emailField && hint) {
        if (isOrg) emailField.setAttribute('aria-describedby', hint.id);
        else emailField.removeAttribute('aria-describedby');
      }
      if (submit) submit.textContent = submit.getAttribute(isOrg ? 'data-label-org' : 'data-label-personal');
      if (submit && submit.getAttribute('aria-busy') !== 'true') submit.disabled = false;
      /* OPP-01 #2/#13: what comes next, and the steps, follow the tile. */
      each(screen.querySelectorAll('[data-pbm-next]'), function (n) {
        n.hidden = n.getAttribute('data-pbm-next') !== (isOrg ? 'org' : 'personal');
      });
      var steps = root.querySelector('[data-pbm-steps]');
      if (steps) steps.hidden = !isOrg;
      if (remember) {
        setCookie('pbm_type', type, 30 * DAY);
        setCookie('pbm_reg', isOrg ? 'organization' : 'personal', 30 * DAY);
      }
    }

    /* Restore a choice made earlier, only when a failed submit re-rendered the form with its errors.
       fix10 (Cora 11:52 PM CT): otherwise nothing is preselected, not even by the browser's own form restore,
       and Continue stays disabled until a tile is picked. */
    var failedSubmit = !!(form && form.querySelector('.errors, [data-pbm-dup-flag], [data-pbm-signup-error-flag]'));
    /* fix12i-j (F-t-assess-90): the sign-up came back without an account (?s=1, no customer) and without a taken-email error. */
    var cameBack = query().s === '1' && !(form && form.querySelector('[data-pbm-dup-flag]'));
    var saved = getCookie('pbm_type');
    var draft = null;
    try { draft = JSON.parse(sessionStorage.getItem(DRAFT_KEY) || 'null'); } catch (e6) { draft = null; }
    if (draft && !(draft.ts && Date.now() - draft.ts >= 0 && Date.now() - draft.ts < 30 * 60 * 1000)) {
      draft = null;
      try { sessionStorage.removeItem(DRAFT_KEY); } catch (e7) { /* ignore */ }
    }
    /* fix12i-j (F-t-assess-90): after any failed sign-up (an error re-render or a return without an account) the typed names,
       the email and the tile come back from this tab's draft (empty fields only; never the password). */
    function fillDraft() {
      var fillIn = function (name, v) { var el = form.querySelector('input[name="' + name + '"]'); if (el && !el.value && v) el.value = v; };
      fillIn('customer[first_name]', draft.f);
      fillIn('customer[last_name]', draft.l);
      fillIn('customer[email]', recalledEmail());
    }
    if (failedSubmit) {
      if (saved && !checkedValue(tiles)) each(tiles, function (t) { if (t.value === saved) t.checked = true; });
      if (draft && form) {
        if (draft.tile && !checkedValue(tiles)) each(tiles, function (t) { if (t.value === draft.tile) t.checked = true; });
        fillDraft();
      }
    } else if (draft && form) {
      /* fix12 (F-t-assess-01): this tab submitted and came back without an account: put the typed values back. */
      each(tiles, function (t) { t.checked = !!draft.tile && t.value === draft.tile; });
      fillDraft();
    } else {
      each(tiles, function (t) { t.checked = false; });
    }
    apply(checkedValue(tiles), false);
    if (submit && !checkedValue(tiles)) submit.disabled = true;
    /* fix10 (F-04/F-08): Shopify binds its invisible hCaptcha to the form only on the first focus inside it, and that binding loads a
       script. Tiles sit outside the form, so a buyer could pick a tile, use autofill and click Continue before the binding finished;
       Shopify then swallowed that first submit. Start the binding now instead. */
    if (form) { try { form.dispatchEvent(new FocusEvent('focusin', { bubbles: true })); } catch (e0) { /* old browser: binds on first focus */ } }

    each(tiles, function (t) {
      t.addEventListener('change', function () { apply(t, true); });
    });

    if (form) {
      /* fix10 (F-t-growth-04): one submit at a time. Shopify's invisible hCaptcha runs its own submit listener
         (it calls execute, then posts the form itself). A second Continue while that is open would start a
         second execute, so it is swallowed until the first attempt settles or times out. */
      var inFlight = false, stallTimer = null, clearTimer = null, capTimer = null;
      var stallEl = screen.querySelector('[data-pbm-stall]');
      function captchaFrame() {
        var fs = document.querySelectorAll('iframe[src*="hcaptcha"]');
        for (var i = 0; i < fs.length; i++) {
          var f = fs[i];
          if ((f.getAttribute('title') || '').toLowerCase().indexOf('challenge') === -1) continue;
          var r = f.getBoundingClientRect(), cs = window.getComputedStyle(f);
          if (r.width > 0 && r.height > 0 && cs.visibility !== 'hidden' && cs.display !== 'none') return f;
        }
        return null;
      }
      function settle(showStall) {
        inFlight = false;
        clearTimeout(stallTimer); clearInterval(capTimer); clearTimeout(clearTimer);
        busy(submit, false);
        show(stallEl, !!showStall);
        /* The note clears itself, so the page never stays on "Still working…". */
        if (showStall) clearTimer = setTimeout(function () { show(stallEl, false); }, 20000);
      }
      /* fix12i-j (F-t-assess-90): no network while the sign-up is in flight: the general sign-up error, not the stall note. */
      var signupErr = root.querySelector('[data-pbm-signup-error]');
      function networkFailed() {
        if (!inFlight) return;
        settle(false);
        show(signupErr, true);
        try { signupErr.focus({ preventScroll: false }); } catch (eN) { /* ignore */ }
      }
      window.addEventListener('offline', networkFailed);
      /* Back/forward cache: a page restored mid-submit starts clean. */
      window.addEventListener('pageshow', function (ev) { if (ev.persisted) settle(false); });
      form.addEventListener('input', function () { if (!inFlight) show(stallEl, false); });
      /* fix12 (F-t-assess-01): a stalled first submit must not lose what was typed. The page itself never clears
         a field; if the page is left and reloaded (Shopify's check page, a reload, Back without the back/forward
         cache), the names, the email and the tile come back in this tab for 30 minutes. Never the password. */
      function saveDraft() {
        try {
          var c = checkedValue(tiles);
          var f = form.querySelector('input[name="customer[first_name]"]'), l = form.querySelector('input[name="customer[last_name]"]');
          sessionStorage.setItem(DRAFT_KEY, JSON.stringify({ f: f ? f.value : '', l: l ? l.value : '', tile: c ? c.value : '', ts: Date.now() }));
        } catch (e5) { /* storage blocked */ }
      }
      /* Shopify fires 'formdata' when the form is really submitted (its captcha calls the native submit). */
      form.addEventListener('formdata', function () { setCookie('pbm_dup', String(Date.now()), 5 * 60); });
      /* The note's own links are the buyer's choice, not Shopify's answer: no duplicate-email notice after them. */
      each(screen.querySelectorAll('[data-pbm-stall] a'), function (a) {
        a.addEventListener('click', function () { delCookie('pbm_dup'); });
      });
      var replaying = false;
      form.addEventListener('submit', function (e) {
        /* Our own one-time re-send (below) goes straight through to Shopify's captcha. */
        if (replaying) { replaying = false; return; }
        if (inFlight) { e.preventDefault(); e.stopImmediatePropagation(); return; }
        var chosen = checkedValue(tiles);
        if (!chosen) {
          e.preventDefault();
          if (tiles[0] && tiles[0].reportValidity) tiles[0].reportValidity();
          else if (tiles[0]) tiles[0].focus();
          return;
        }
        /* minlength only binds typed input; autofill or pasted values can bypass it. */
        var pw = form.querySelector('input[name="customer[password]"]');
        if (pw && pw.value.length < 5) {
          e.preventDefault();
          pw.setCustomValidity(pw.getAttribute('data-pbm-short') || ' ');
          pw.reportValidity();
          pw.addEventListener('input', function clear() { pw.setCustomValidity(''); pw.removeEventListener('input', clear); });
          return;
        }
        apply(chosen, true);
        setCookie('pbm_signup', 'password', 30 * DAY);
        /* fix12 (F-t-assess-01): the login page's duplicate-email marker is set on 'formdata' (below), when the
           form really posts, not here: a submit that stalled before posting and then "Sign in" said
           "That email already has an account" for an email that had none. */
        saveDraft();
        if (navigator.onLine === false) { e.preventDefault(); inFlight = true; networkFailed(); return; }
        inFlight = true;
        show(stallEl, false);
        show(signupErr, false);
        busy(submit, true);
        /* fix10 (F-08): if Shopify swallowed this submit because its captcha was still binding (prevented, but the form is not bound),
           send it again exactly once as soon as the binding is there. A bound form or a real navigation needs nothing. */
        setTimeout(function () {
          if (!e.defaultPrevented || form.getAttribute('data-hcaptcha-bound')) return;
          var waited = 0;
          var iv = setInterval(function () {
            waited += 100;
            if (form.getAttribute('data-hcaptcha-bound')) {
              clearInterval(iv);
              replaying = true;
              try { if (form.requestSubmit) form.requestSubmit(); else form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true })); }
              catch (e4) { replaying = false; }
            } else if (waited >= 7000) { clearInterval(iv); }
          }, 100);
        }, 0);
        var emailIn = form.querySelector('input[name="customer[email]"]');
        if (emailIn) rememberEmail(emailIn.value);
        /* fix10: when the hCaptcha puzzle opens, bring it into view (it can open above the fold). */
        var scrolled = false, capSeen = false, everCap = false, polls = 0;
        clearInterval(capTimer);
        capTimer = setInterval(function () {
          polls++;
          var f = captchaFrame();
          if (f) {
            capSeen = true; everCap = true;
            if (!scrolled) {
              scrolled = true;
              var r = f.getBoundingClientRect();
              if (r.top < 0 || r.bottom > window.innerHeight) { try { f.scrollIntoView({ block: 'center' }); } catch (e2) { f.scrollIntoView(); } }
            }
          } else if (capSeen) {
            /* Puzzle closed without leaving the page (cancelled or failed): give the button back now. */
            capSeen = false; scrolled = false;
            setTimeout(function () { if (inFlight) settle(false); }, 1500);
          }
          if (polls > 400) clearInterval(capTimer);
        }, 300);
        /* If the browser stays on the page (validation elsewhere), give the button back. OPP-01 #1: and say
           what may be going on (an existing email, or a slow check) instead of leaving a grey button. fix10: not while
           the puzzle is open (the buyer is still answering it); re-checked every 8 seconds. */
        clearTimeout(stallTimer);
        (function wait() {
          stallTimer = setTimeout(function () {
            if (captchaFrame()) { wait(); return; }
            if (navigator.onLine === false) { networkFailed(); return; }
            /* After a puzzle was shown and closed, just give the button back; the note is for a page that never moved. */
            settle(!everCap);
          }, 8000);
        })();
      });
    }

    /* No Sign in with Shop button (the store setting is off): hide the notice's shortcut to it. */
    var shopWrap = root.querySelector('#pbm-shop');
    if (!shopWrap || !shopWrap.children.length) {
      each(root.querySelectorAll('[data-pbm-to-shop]'), function (a) { a.hidden = true; });
    }

    /* Sign in with Shop: remember the signup path and the tile before Shop takes over. */
    each(root.querySelectorAll('[data-pbm-shop]'), function (wrap) {
      wrap.addEventListener('click', function () {
        setCookie('pbm_signup', 'shop', 30 * DAY);
        var chosen = checkedValue(tiles);
        if (chosen) apply(chosen, true);
      }, true);
    });

    /* fix12i-j (F-t-assess-90): the duplicate-email notice needs a real taken-email error (the Liquid flag: field email, taken /
       already message). A return to ?s=1 without an account and without that error (Shopify throttled the sign-up: "Too many
       attempts", a failed captcha, anything unknown) and a form-level error show the general sign-up error instead. */
    var dup = root.querySelector('[data-pbm-dup]');
    var gen = root.querySelector('[data-pbm-signup-error]');
    if (dup && root.querySelector('[data-pbm-dup-flag]')) {
      show(dup, true);
      delCookie('pbm_dup');
      try { dup.focus({ preventScroll: false }); } catch (e) { /* focus is a nicety */ }
    } else if (gen && (cameBack || root.querySelector('[data-pbm-signup-error-flag]'))) {
      show(dup, false);
      show(gen, true);
      delCookie('pbm_dup');
      try { gen.focus({ preventScroll: false }); } catch (e) { /* focus is a nicety */ }
    }
  }

  /* ------------------------------------------------------------ Screen 2 */

  function initScreen2(root) {
    var screen = root.querySelector('[data-pbm-screen="org"]');
    if (!screen || screen.getAttribute('data-pbm-ready')) return;
    screen.setAttribute('data-pbm-ready', '1');

    var tiles = screen.querySelectorAll('input[name="pbm_org_type"]');
    var form = screen.querySelector('form');
    var submit = screen.querySelector('[data-pbm-finish]');
    var error = screen.querySelector('[data-pbm-org-error]');

    /* fix12i-f (GL-F-42): bring back this tab's draft before the defaults below (empty fields only). */
    var draftCid = root.getAttribute('data-pbm-customer') || '';
    var draftKey = DRAFT_ORG + draftCid;
    if (form && draftCid) {
      try {
        var draft = JSON.parse(sessionStorage.getItem(draftKey) || 'null');
        if (draft && typeof draft === 'object') {
          var serverPicked = !!checkedValue(tiles);
          /* fix12i-g (F-44): the type tiles are outside the form, so the draft keeps them too. Unless the store has typed the
             account (type:* tag, the server checked a tile), the draft's type wins over the sign-up tile default below. */
          if (!serverPicked && orgTypes().indexOf(draft.pbm_org_type) > -1) {
            each(tiles, function (t) { t.checked = t.value === draft.pbm_org_type; });
          }
          each(form.querySelectorAll('input, select, textarea'), function (el) {
            if (!draftable(el) || !Object.prototype.hasOwnProperty.call(draft, el.name)) return;
            var dv = String(draft[el.name] == null ? '' : draft[el.name]);
            if (el.type === 'radio') { if (!serverPicked && el.value === dv) el.checked = true; return; }
            if (!String(el.value || '').trim() && dv) el.value = dv;
          });
        }
      } catch (eD) { /* storage blocked or a bad draft: the form stays as served */ }
      var saveDraft = function () {
        var d = {};
        each(form.querySelectorAll('input, select, textarea'), function (el) {
          if (!draftable(el)) return;
          if (el.type === 'radio') { if (el.checked) d[el.name] = el.value; return; }
          d[el.name] = String(el.value || '');
        });
        var tv = checkedValue(tiles);
        if (tv) d.pbm_org_type = tv.value;
        try { sessionStorage.setItem(draftKey, JSON.stringify(d)); } catch (eS) { /* storage full or blocked */ }
      };
      form.addEventListener('input', saveDraft);
      form.addEventListener('change', saveDraft);
      each(tiles, function (t) { t.addEventListener('change', saveDraft); });
    }
    /* fix11p (F-16): the account's own signup:<tile> first; the browser-wide cookie may be another account's pick.
       fix12i-f (GL-F-41): a type this customer already saved on this form comes first. */
    var pre = savedOrgType(root) || root.getAttribute('data-pbm-signup') || getCookie('pbm_type') || root.getAttribute('data-pbm-type') || '';
    if (orgTypes().indexOf(pre) > -1 && !checkedValue(tiles)) {
      each(tiles, function (t) { if (t.value === pre) t.checked = true; });
    }
    each(tiles, function (t) {
      t.addEventListener('change', function () {
        setCookie('pbm_type', t.value, 30 * DAY);
        setCookie('pbm_reg', 'organization', 30 * DAY);
      });
    });

    /* OPP-01 #14: Skip for now. No forward back to this form on /account for 30 days; the account page shows a banner. */
    each(screen.querySelectorAll('[data-pbm-skip-org]'), function (a) {
      a.addEventListener('click', function () {
        /* fix12g (F-16): the skip belongs to this customer, not the browser (an earlier "1" muted every later account). */
        setCookie('pbm_skip_org', root.getAttribute('data-pbm-customer') || '1', 30 * DAY);
        try { sessionStorage.setItem('pbm_fwd', '1'); } catch (e) { /* ignore */ }
        var ret = safeReturn(getCookie('pbm_return'));
        if (ret) a.setAttribute('href', ret);
      });
    });

    if (!form) return;

    function field(name) {
      var el = form.querySelector('[name="' + name + '"]');
      return el ? String(el.value || '').trim() : '';
    }

    function stateCode() {
      var sel = form.querySelector('[name="address[province]"]');
      if (!sel || sel.selectedIndex < 0) return '';
      var opt = sel.options[sel.selectedIndex];
      return (opt && (opt.getAttribute('data-code') || opt.value)) || '';
    }

    /* After the POST, re-render this section and read the default address's Company from it:
       true = saved as the default, false = not saved, null = cannot check (offline, 429, 5xx). */
    function htmlEscape(v) {
      return String(v).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    }
    function verifySaved() {
      var sectionId = root.getAttribute('data-pbm-section');
      if (!sectionId) return Promise.resolve(null);
      var url = location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&pbm_t=' + Date.now();
      var want = 'data-pbm-default-company="' + htmlEscape(field('address[company]')) + '"';
      return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.ok ? r.text() : null; })
        .then(function (html) { return html === null ? null : html.indexOf(want) > -1; })
        .catch(function () { return null; });
    }

    function beacon(type) {
      var url = serviceUrl(root.getAttribute('data-pbm-service'), 'org');
      var token = priv(root).org_token;
      if (!url || !token) return Promise.resolve(null);
      /* This session has now sent its org beacon: the card must not send a second one. */
      try { sessionStorage.setItem('pbm_card_beacon_' + root.getAttribute('data-pbm-customer'), '1'); } catch (e) { /* ignore */ }
      var payload = {
        customer_id: root.getAttribute('data-pbm-customer'),
        email: root.getAttribute('data-pbm-email') || '',
        first_name: field('address[first_name]'),
        last_name: field('address[last_name]'),
        type: type,
        company: field('address[company]'),
        street: field('address[address1]'),
        suite: field('address[address2]'),
        city: field('address[city]'),
        state: stateCode(),
        zip: field('address[zip]'),
        phone: field('address[phone]'),
        /* "shop" only while the Shop button is on (LJ 9/27: off by default), never from an old cookie. */
        signup: (root.getAttribute('data-pbm-shop-login') === 'on' && getCookie('pbm_signup') === 'shop') ? 'shop' : 'password',
        return: safeReturn(getCookie('pbm_return')),
        token: token,
        email_token: priv(root).email_token || '',
        /* fix12i-g (GL-F-41): the account's signup:<tile> (read only), so the service logs it next to the saved type. */
        signup_tile: root.getAttribute('data-pbm-signup') || ''
      };
      salesfirstPayload(root, payload);
      var waitMs = root.getAttribute('data-pbm-salesfirst') === 'on' ? 8000 : 5000;
      return postService(url, payload, { keepalive: true, timeout: waitMs }).then(function (res) {
        rememberTier(root, res);
        return res;
      }).catch(function () { return null; });
    }

    form.addEventListener('submit', function (e) {
      var chosen = checkedValue(tiles);
      if (!chosen) {
        e.preventDefault();
        if (tiles[0] && tiles[0].reportValidity) tiles[0].reportValidity();
        return;
      }
      if (typeof window.fetch !== 'function' || typeof URLSearchParams !== 'function') return; /* plain POST */
      e.preventDefault();
      show(error, false);
      busy(submit, true);
      setCookie('pbm_type', chosen.value, 30 * DAY);
      setCookie('pbm_reg', 'organization', 30 * DAY);

      var body = new URLSearchParams(new FormData(form));
      fetch(form.action, {
        method: 'POST',
        body: body,
        credentials: 'same-origin',
        redirect: 'follow'
      }).then(function (r) {
        if (!r.ok) throw new Error('address_http_' + r.status);
        /* Shopify answered the POST: an uncheckable result (null) counts as saved. */
        return verifySaved().then(function (saved) { return saved !== false; });
      }, function (netErr) {
        /* The POST may have reached Shopify even though the answer was lost: check first, so a
           retry never creates a second address. Only a confirmed-unsaved address falls back to
           a plain post; if nothing can be checked, keep the form and show the error. */
        return verifySaved().then(function (saved) {
          if (saved === true) return true;
          if (saved === false) throw netErr;
          throw new Error('address_unknown');
        });
      }).then(function (saved) {
        if (!saved) throw new Error('address_not_saved');
        /* fix12f (F-24): the spinner waited for the service's org answer (up to 8 s sales-first, the service takes about
           9 s), so Save and Continue sat about 10 s. The beacon is a keepalive request and finishes after the page moves
           on; wait at most 1.5 s for it (a quick answer still stores the tier for the card). */
        return Promise.race([beacon(chosen.value), new Promise(function (r) { setTimeout(r, 1500); })]);
      }).then(function () {
        var ret = safeReturn(getCookie('pbm_return'));
        delCookie('pbm_return');
        delCookie('pbm_dup');
        /* This browser has completed Screen 2 for this customer: show the card from now on. */
        setCookie('pbm_org_done', root.getAttribute('data-pbm-customer') || '1', 30 * DAY);
        /* fix12i-f (GL-F-41): the saved type, for this customer only, until the store tags the account. GL-F-42: saved, so no draft. */
        if (root.getAttribute('data-pbm-customer')) setCookie('pbm_org_type', root.getAttribute('data-pbm-customer') + ':' + chosen.value, 30 * DAY);
        try { sessionStorage.removeItem(DRAFT_ORG + (root.getAttribute('data-pbm-customer') || '')); } catch (eR) { /* ignore */ }
        /* fix12i-g (F-45): /account after this save asks the card route like any load, headline held, until the service names
           the tier (it classifies in about 9 s), so the unverified state shows the same headline as on a later sign-in. A tier
           this tab learned before the save no longer applies. */
        try {
          sessionStorage.removeItem('pbm_tier_' + (root.getAttribute('data-pbm-customer') || ''));
          sessionStorage.setItem(ORG_SAVED + (root.getAttribute('data-pbm-customer') || ''), String(Date.now()));
        } catch (eT2) { /* ignore */ }
        /* OPP-01 #33: land on /account (the card shows there), not on /pages/register.
           fix12f (F-26): a cart return only when the cart still has items (/cart.js item_count > 0); an empty cart, or a
           cart that cannot be read within 1.5 s, goes to /account. Every organization tile. */
        var isCart = !!ret && /^\/cart(\/|\?|#|$)/.test(ret);
        if (!isCart) { location.href = ret || '/account'; return; }
        var gone = false;
        function go(dest) { if (gone) return; gone = true; location.href = dest; }
        setTimeout(function () { go('/account'); }, 1500);
        fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
          .then(function (r) { return r.ok ? r.json() : null; })
          .then(function (c) { go(c && c.item_count > 0 ? ret : '/account'); }, function () { go('/account'); });
      }).catch(function (err) {
        busy(submit, false);
        if (err && err.name === 'TypeError') {
          /* Network trouble and the address is not saved: fall back to Shopify's own form post. */
          form.submit();
          return;
        }
        show(error, true);
        try { error.focus(); } catch (e2) { /* ignore */ }
      });
    });
  }

  /* ---------------------------------------------- personal card & add organization */

  /* fix12 (F-08; v8 THEME-NOTES §6): the Personal Use "One Step Before Rx Items Ship" screen tells the service the
     signed-in customer's Shopify email (with its email_token) and name, once per browser session. Fire and forget:
     nothing is shown either way. The service saves only what is missing and sends account-ready at most once. */
  function personalBeacon(root) {
    var el = root.querySelector('[data-pbm-personal-beacon]');
    if (!el || el.hidden || el.closest('[hidden]')) return;
    var cid = root.getAttribute('data-pbm-customer');
    var url = serviceUrl(root.getAttribute('data-pbm-service'), 'personal');
    var p = priv(root);
    if (!cid || !url || !p.card_token || !p.email_token) return;
    var flag = 'pbm_personal_beacon_' + cid;
    try { if (sessionStorage.getItem(flag)) return; sessionStorage.setItem(flag, '1'); } catch (e) { /* send anyway */ }
    var email = String(root.getAttribute('data-pbm-email') || '').trim().toLowerCase();
    postWithFreshToken(root, url, function (q) {
      return {
        customer_id: cid, token: q.card_token, email: email, email_token: q.email_token || '',
        first_name: el.getAttribute('data-pbm-first-name') || '', last_name: el.getAttribute('data-pbm-last-name') || ''
      };
    }, { keepalive: true, timeout: 10000 }).catch(noop);
  }

  function initPersonal(root) {
    personalBeacon(root);
    each(root.querySelectorAll('[data-pbm-add-org]'), function (a) {
      if (a.getAttribute('data-pbm-ready')) return;
      a.setAttribute('data-pbm-ready', '1');
      a.addEventListener('click', function (e) {
        setCookie('pbm_reg', 'organization', 30 * DAY);
        /* Screen 2 when there is no Company yet; the full card when a Company already exists. */
        var org = root.querySelector('[data-pbm-screen="org"]') || root.querySelector('[data-pbm-screen="card"]');
        var personal = root.querySelector('[data-pbm-screen="personal"]');
        if (org && personal) {
          e.preventDefault();
          show(personal, false);
          show(org, true);
          var first = org.querySelector('input:not([type="hidden"]), select');
          if (first) try { first.focus(); } catch (e2) { /* ignore */ }
        }
      });
    });
  }

  /* ------------------------------------------------------------------- the card */

  var RANK_FINAL = {
    organization: { durable: ['verified', 'linked', 'declined'], lagging: ['verifying', 'verified'] },
    authorization: { durable: ['on_file', 'on_file_nodate', 'exempt'], lagging: ['none', 'sent', 'signed', 'verifying', 'on_file', 'on_file_nodate'] },
    tax: { durable: ['verified'], lagging: ['none', 'received'] }
  };

  /* The service is fresher for things it did itself (sent, received); the metafields are
     written by the desk. Never let a lagging service state hide a final durable state. */
  function merge(kind, durable, live) {
    if (!live) return durable;
    if (!durable) return live;
    var r = RANK_FINAL[kind];
    if (r && r.durable.indexOf(durable) > -1 && r.lagging.indexOf(live) > -1) return durable;
    return live;
  }

  function setRow(card, row, state, vars) {
    var el = card.querySelector('[data-pbm-row="' + row + '"]');
    if (!el || !state) return;
    var variants = el.querySelectorAll('[data-pbm-s]');
    var match = false;
    each(variants, function (v) {
      var on = (' ' + v.getAttribute('data-pbm-s') + ' ').indexOf(' ' + state + ' ') > -1;
      if (on) match = true;
    });
    if (!match) return;
    each(variants, function (v) {
      v.hidden = (' ' + v.getAttribute('data-pbm-s') + ' ').indexOf(' ' + state + ' ') === -1;
    });
    el.setAttribute('data-pbm-current', state);
    if (vars) {
      each(el.querySelectorAll('[data-pbm-v]'), function (n) {
        var k = n.getAttribute('data-pbm-v');
        if (vars[k]) n.textContent = vars[k];
      });
    }
    var waits = (el.getAttribute('data-pbm-waits') || '').split(' ');
    each(el.querySelectorAll('[data-pbm-wait]'), function (n) { show(n, waits.indexOf(state) > -1); });
    if (row === 'authorization') {
      var formWrap = el.querySelector('[data-pbm-director]');
      if (formWrap && !formWrap.getAttribute('data-pbm-open')) show(formWrap, state === 'none' || state === 'expired');
    }
  }

  /* The card token is valid today and yesterday (UTC). A tab left open longer gets bad_token:
     re-render this section once to read fresh tokens, then retry. */
  function refreshTokens(root) {
    var sectionId = root.getAttribute('data-pbm-section');
    if (!sectionId) return Promise.reject(new Error('no_section'));
    var url = location.pathname + '?section_id=' + encodeURIComponent(sectionId) + '&pbm_t=' + Date.now();
    return fetch(url, { credentials: 'same-origin', cache: 'no-store' })
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var m = html.match(/<script type="application\/json" data-pbm-private>([^<]*)<\/script>/);
        if (!m) throw new Error('no_tokens');
        root.__pbmPriv = JSON.parse(m[1]);
        return root.__pbmPriv;
      });
  }

  function postWithFreshToken(root, url, build, opts) {
    return postService(url, build(priv(root)), opts).then(function (res) {
      if (res && res.ok === false && res.error === 'bad_token') {
        return refreshTokens(root).then(function (p) { return postService(url, build(p), opts); });
      }
      return res;
    });
  }

  /* OPP-12: the personal block reuses this form, so bind every copy, including a page with no card. */
  function bindDirectorForms(root, card, service, cid, emailToken, authRow) {
    /* OPP-12 physician flow: "Send to My Doctor or Medical Director" opens and closes the personal referral box. */
    each(root.querySelectorAll('[data-pbm-self-open]'), function (btn) {
      if (btn.getAttribute('data-pbm-bound')) return;
      btn.setAttribute('data-pbm-bound', '1');
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var box = document.getElementById(btn.getAttribute('aria-controls') || '');
        if (!box) return;
        /* fix12f (F-t-assess-32): while its request is open, or after it went through, the box stays as it is. */
        var bf = box.querySelector('[data-pbm-director-form]');
        if (bf && (bf.getAttribute('data-pbm-busy') === '1' || bf.getAttribute('data-pbm-done') === '1')) return;
        var open = box.hidden;
        show(box, open);
        btn.setAttribute('aria-expanded', open ? 'true' : 'false');
        /* OPP-12 loop 10/21 (Assessment Desk: the form reopened blank): the last director this account sent to is filled in. */
        if (open) rxPrefill(box);
        if (open) { var first = box.querySelector('input:not([type="hidden"]), select'); if (first) try { first.focus(); } catch (e2) { /* ignore */ } }
      });
    });
    each(root.querySelectorAll('[data-pbm-director-form]'), function (dForm) {
      if (dForm.getAttribute('data-pbm-bound')) return;
      dForm.setAttribute('data-pbm-bound', '1');
      var dError = dForm.querySelector('[data-pbm-director-error]');
      /* fix12i-e (F-86, Cora): the error line keeps its existing text from the markup, but only while it is shown. It is
         emptied, hidden and aria-hidden at the start of every attempt and on success, so a screen reader or a later
         reveal never meets a stale "didn't go through" next to the confirmation. Same words, restored on a failure. */
      var dErrorText = dError ? dError.textContent : '';
      function dErr(on) {
        if (!dError) return;
        dError.textContent = on ? dErrorText : '';
        dError.hidden = !on;
        if (on) dError.removeAttribute('aria-hidden'); else dError.setAttribute('aria-hidden', 'true');
      }
      dErr(false);
      var dSubmit = dForm.querySelector('[type="submit"]');
      var inFlight = false;
      /* fix12 (F-t-assess-02): the button says "Sending…" while a request is open (Cora 1:06 AM CT wording). */
      if (dSubmit && !dSubmit.hasAttribute('data-label-busy')) dSubmit.setAttribute('data-label-busy', 'Sending…');
      var done = false;
      /* fix12f (F-t-assess-32): extra clicks on Send while a request is open (or after it went through) are dropped
         before anything else sees them: no second submit, no change to the button, the form or the message. */
      function swallow(e) {
        if (!inFlight && !done) return;
        e.preventDefault();
        e.stopImmediatePropagation();
      }
      if (dSubmit) { dSubmit.addEventListener('click', swallow, true); dSubmit.addEventListener('dblclick', swallow, true); }
      dForm.addEventListener('submit', swallow, true);
      /* OPP-12 loop 10/21: Resend in the Rx status line opens this form again after a success (same values, Send enabled).
         A request still open is never reset. The service returns the same req_id for a repeat within 10 minutes. */
      dForm.addEventListener('pbm:reopen', function () {
        /* A request still waiting for its answer (data-pbm-busy) is left alone; after a success inFlight stays set (v8 §4.3). */
        if (dForm.getAttribute('data-pbm-busy') === '1' || (inFlight && !done)) return;
        inFlight = false;
        done = false;
        dForm.removeAttribute('data-pbm-done');
        busy(dSubmit, false);
        if (dSubmit) dSubmit.disabled = false;
        dErr(false);
      });
      /* fix12i-f (F-87): this form never posts natively (action="#" would reload the page; offline that is Chrome's
         offline page). Every submit, including the F-30 replay of a held press, is handled here or dropped. */
      dForm.addEventListener('submit', function (e) { e.preventDefault(); }, true);
      dForm.addEventListener('submit', function (e) {
        e.preventDefault();
        /* fix12 (F-t-assess-02): one request at a time; a second click while one is open does nothing. */
        if (inFlight || done) return;
        if (!dForm.reportValidity()) return;
        var url = serviceUrl(service, 'request_director');
        var token = priv(root).card_token;
        if (!url || !token) { dErr(true); return; }
        var v = function (n) { var el = dForm.querySelector('[name="' + n + '"]'); return el ? String(el.value || '').trim() : ''; };
        var d = root.dataset;
        /* OPP-12 physician flow: the personal box says who the authorization is for. */
        var bw = dForm.closest('[data-pbm-buyer-type]');
        var payload = {
          customer_id: cid,
          token: token,
          director_name: v('director_name'),
          licence_number: v('licence_number'),
          licence_type: v('licence_type'),
          state: v('state'),
          director_email: v('director_email'),
          facility_name: d.pbmCompany || '',
          ship_to: {
            street: d.pbmStreet || '', suite: d.pbmSuite || '', city: d.pbmCity || '',
            state: d.pbmStateCode || '', zip: d.pbmZip || ''
          },
          contact_name: d.pbmName || '',
          contact_phone: d.pbmPhone || '',
          contact_email: d.pbmEmail || '',
          buyer_type: bw ? (bw.getAttribute('data-pbm-buyer-type') || '') : (d.pbmCompany ? 'org' : ''),
          buyer_name: bw ? (bw.getAttribute('data-pbm-buyer-name') || '') : '',
          email_token: emailToken
        };
        dErr(false);
        inFlight = true;
        dForm.setAttribute('data-pbm-busy', '1');
        busy(dSubmit, true);
        var clickedAt = Date.now();
        function finish() { inFlight = false; dForm.removeAttribute('data-pbm-busy'); busy(dSubmit, false); }
        function sent(res) {
          /* fix12f (F-t-assess-32): a success ends in the success state once; a late second answer changes nothing. */
          if (done) return;
          done = true;
          dErr(false);
          dForm.setAttribute('data-pbm-done', '1');
          dForm.removeAttribute('data-pbm-busy');
          /* v8 THEME-NOTES §4.3: the button stays disabled after a success (the form closes); inFlight stays set. */
          busy(dSubmit, false);
          if (dSubmit) dSubmit.disabled = true;
          var wrap = dForm.closest('[data-pbm-director]');
          if (wrap) { wrap.removeAttribute('data-pbm-open'); show(wrap, false); }
          if (wrap && wrap.id) each(root.querySelectorAll('[data-pbm-self-open][aria-controls="' + wrap.id + '"]'), function (b2) { b2.setAttribute('aria-expanded', 'false'); });
          /* fix12f (F-t-assess-32): the thank-you line right after this form's own box (never another copy's). */
          var sentEl = null;
          for (var sib = wrap ? wrap.nextElementSibling : null; sib && !sentEl; sib = sib.nextElementSibling) {
            if (sib.hasAttribute('data-pbm-director-sent')) sentEl = sib;
            else if (sib.hasAttribute('data-pbm-director')) break;
          }
          if (!sentEl && wrap && wrap.parentElement) sentEl = wrap.parentElement.querySelector('[data-pbm-director-sent]');
          if (sentEl) show(sentEl, true);
          PBM.lastDirector = { name: payload.director_name, email: payload.director_email, state: payload.state, type: payload.licence_type, number: payload.licence_number };
          /* OPP-12 loop 10/21: the Rx status line says "Sent to ..." right away (no reload). */
          rxStatus(root, { authorization: { status: 'sent', director_name: payload.director_name, licence_type: payload.licence_type, sent_at: res.sent_at || new Date().toISOString() } }, true);
          if (!card) return;
          setRow(card, 'authorization', 'sent', {
            director_name: directorDisplay(payload.director_name),
            sent_at: formatDate(res.sent_at || new Date().toISOString())
          });
          /* The service's emails print "Dr" only for MD, DO, DDS, DMD, DPM (Cora 9/27; DPM LJ 10/19); match them. */
          each(card.querySelectorAll('[data-pbm-dr]'), function (n) {
            n.hidden = ['NP', 'CNM', 'LM', 'PA', 'RPH'].indexOf(payload.licence_type) > -1;
          });
          /* fix2 (F-56): a card showing the V2 Rx block keeps it (its status line says "Sent to ..."); the old row stays hidden. */
          var orgRxS = card.querySelector('[data-pbm-org-rx]');
          if (authRow && !(orgRxS && !orgRxS.hidden)) { authRow.hidden = false; try { authRow.focus(); } catch (e3) { /* ignore */ } }
        }
        function failed() { if (done) return; finish(); dErr(true); }
        /* fix12 (F-t-assess-02; v8 THEME-NOTES §1/§4): the service answers only after doPost ends; v7 took 8.7-9.7 s of
           server time plus start-up and redirect, past the old 12 s abort, so a request that went through showed
           "That didn't go through" and the next Send was the service's repeat. Now: 30 s; on a timeout or network error
           the SAME payload is posted once more (v8: a repeat within 10 minutes returns the same req_id and sends no
           email); if that is also unknown, the card route is asked whether the request was recorded before any error.
           A definite refusal (busy, mail_failed, a field error) shows the error. bad_token still refreshes once. */
        var opts = { timeout: 30000, strict: true };
        function answer(res) {
          if (res && res.ok === true) { sent(res); return; }
          if (!res || res.unknown || res.error === 'server_error') { confirmSent(); return; }
          failed();
        }
        postWithFreshToken(root, url, function (p) {
          payload.token = p.card_token;
          payload.email_token = p.email_token || '';
          return payload;
        }, opts).then(function (res) { answer(res); }, function () {
          postService(url, payload, opts).then(function (res) { answer(res); }, function () { confirmSent(); });
        });
        function confirmSent() {
          var cardUrl = serviceUrl(service, 'card', { cid: cid, t: priv(root).card_token });
          if (!cardUrl) { failed(); return; }
          var want = String(payload.director_name || '').replace(/^dr\.?\s+/i, '').trim().toLowerCase();
          var tries = 0;
          function poll() {
            tries++;
            /* fix12i-f (F-87, Cora): a card check that gets no answer at all (offline, network down, an unreadable
               reply) ends the attempt now: the error shows and Send is enabled again, with no navigation. Waiting
               20 x 4 s with no answer left the error empty and hidden for over a minute. Only a check that timed out
               (AbortError) or answered "not recorded yet" keeps polling, as before. A Send after this error repeats the
               same request: the service returns the same req_id for a repeat within 10 minutes and sends no second email. */
            getService(cardUrl, 10000).then(function (res) {
              var a = res && res.ok !== false ? (res.card || res).authorization : null;
              var at = a && a.sent_at ? Date.parse(a.sent_at) : NaN;
              if (a && ['sent', 'signed', 'verifying', 'on_file'].indexOf(String(a.status || '')) > -1 &&
                  String(a.director_name || '').trim().toLowerCase() === want && at >= clickedAt - 120000) {
                sent({ ok: true, req_id: a.req_id, sent_at: a.sent_at });
                return;
              }
              next();
            }, function (err) { if (err && err.name === 'AbortError') next(); else failed(); });
          }
          function next() { if (tries >= 20) failed(); else setTimeout(poll, 4000); }
          poll();
        }
      });
    });
  }

  /* OPP-12 loop 10/21 (Assessment Desk LIVE run, /account PARTIAL): the Rx block's status lines (snippets/pbm-rx-actions.liquid,
     status: true) from the service's card route: the authorization form sent (with Resend), signed online or uploaded, on file,
     and a prescriber license uploaded (service v11.19 adds card.licence). Words come from the block's data-st-* attributes. */
  var RX_NODR = ['NP', 'CNM', 'LM', 'PA', 'RPH', 'OTHER'];
  function rxName(a) {
    var n = directorDisplay(a.director_name || '');
    if (!n) return '';
    return a.licence_type && RX_NODR.indexOf(String(a.licence_type).toUpperCase()) > -1 ? n : 'Dr. ' + n;
  }
  function rxLine(box, which, key, vars) {
    var line = box.querySelector('[data-pbm-rxs="' + which + '"]');
    if (!line) return false;
    var t = key ? (box.getAttribute('data-st-' + key) || '') : '';
    if (!t) { line.hidden = true; return false; }
    t = t.replace('__NAME__', vars.name || '').replace(/ on __DATE__/, vars.date ? ' on ' + vars.date : '').replace('__DATE__', vars.date || '');
    var txt = line.querySelector('[data-pbm-rxs-text]');
    if (txt) txt.textContent = t;
    var rb = line.querySelector('[data-pbm-rx-resend]');
    if (rb) rb.hidden = key !== 'sent';
    line.hidden = false;
    return true;
  }
  function rxStatus(root, c, partial) {
    if (!c) return;
    var a = c.authorization || {};
    if (a.director_name) {
      PBM.rxAuth = { name: a.director_name, email: a.director_email || '', state: a.state || '', type: a.licence_type || '', number: a.licence_number || '' };
    }
    each(root.querySelectorAll('[data-pbm-rx-status]'), function (box) {
      var st = String(a.status || ''), key = '', date = '';
      if (st === 'sent') { key = 'sent'; date = a.sent_at; }
      else if (st === 'signed' || st === 'verifying') { key = a.paper ? 'uploaded' : 'signed'; date = a.signed_at || a.uploaded_at || ''; }
      else if (st === 'on_file' || st === 'on_file_nodate') key = 'on-file';
      var f = rxLine(box, 'form', key, { name: rxName(a), date: formatDate(date) });
      var l = false;
      if (partial) l = !box.querySelector('[data-pbm-rxs="licence"]:not([hidden])') ? false : true;
      else {
        var lic = c.licence || {};
        l = rxLine(box, 'licence', lic.status === 'uploaded' ? 'uploaded' : '', { date: formatDate(lic.uploaded_at) });
      }
      box.hidden = !(f || l);
    });
  }
  function rxPrefill(scope) {
    var last = PBM.lastDirector || PBM.rxAuth;
    if (!last) return;
    var map = { director_name: directorDisplay(last.name || ''), director_email: last.email, state: last.state, licence_type: last.type, licence_number: last.number };
    Object.keys(map).forEach(function (k) {
      var el = scope.querySelector('[name="' + k + '"]');
      if (el && !el.value && map[k]) el.value = map[k];
    });
  }
  /* Opens the Send box of one Rx block (box mode: its own box; fold mode: the card's director fold), reset and prefilled. */
  function rxOpenSend(rxa) {
    var btn = rxa.querySelector('[data-pbm-self-open]');
    var box = btn ? document.getElementById(btn.getAttribute('aria-controls') || '') : null;
    var fold = null;
    if (!box) {
      var fa = rxa.querySelector('[data-pbm-open-fold]');
      var h = fa ? fa.getAttribute('href') : '';
      var target = null;
      try { target = h && h.charAt(0) === '#' ? document.querySelector(h) : null; } catch (e) { target = null; }
      fold = target ? target.querySelector('details.pbm-fold') : null;
      box = target;
      if (!box) return false;
    }
    var f = box.querySelector('[data-pbm-director-form]');
    if (f) { try { f.dispatchEvent(new CustomEvent('pbm:reopen')); } catch (e1) { /* ignore */ } }
    for (var sib = box.nextElementSibling; sib; sib = sib.nextElementSibling) {
      if (sib.hasAttribute('data-pbm-director-sent')) { sib.hidden = true; break; }
      if (sib.hasAttribute('data-pbm-director')) break;
    }
    if (btn) { show(box, true); btn.setAttribute('aria-expanded', 'true'); }
    if (fold && !fold.hidden) fold.open = true;
    rxPrefill(box);
    try { box.scrollIntoView({ block: 'center' }); } catch (e2) { /* ignore */ }
    var first = box.querySelector('input:not([type="hidden"]), select');
    if (first) try { first.focus({ preventScroll: true }); } catch (e3) { /* ignore */ }
    return true;
  }
  function initRxStatus(root) {
    each(root.querySelectorAll('[data-pbm-rx-resend]'), function (b) {
      if (b.getAttribute('data-pbm-bound')) return;
      b.setAttribute('data-pbm-bound', '1');
      b.addEventListener('click', function (e) {
        e.preventDefault();
        var rxa = b.closest('[data-pbm-rxa]');
        if (rxa) rxOpenSend(rxa);
      });
    });
    /* The cart's "Send to My Doctor or Medical Director" links to /account#pbm-send: open the first visible Send box once. */
    if (location.hash !== '#pbm-send' || PBM.rxHashDone) return;
    PBM.rxHashDone = true;
    setTimeout(function () {
      var list = root.querySelectorAll('[data-pbm-rxa]');
      for (var i = 0; i < list.length; i++) {
        if (list[i].offsetParent === null) continue;
        if (rxOpenSend(list[i])) return;
      }
      var rs = root.querySelector('[data-pbm-resend]');
      if (rs && rs.offsetParent !== null) rs.click();
    }, 0);
  }

  /* fix12g (F-53, Cora): the checklist's Skip for Now (org card on /account and /pages/register) dismisses the tax item
     with no navigation; remembered for this customer in this browser (pbm_tax_skip, 30 days). The checklist hides when
     nothing is left in it; Skip for Now hides once the tax item is gone (the authorization item is required). The
     organization form's own Skip for Now (snippets/pbm-org-screen.liquid, data-pbm-skip-org) is unchanged. */
  function initChecklistSkip(root, card) {
    var cl = card.querySelector('.pbm-checklist');
    if (!cl) return;
    var cid = root.getAttribute('data-pbm-customer') || '1';
    function apply() {
      var tax = cl.querySelector('[data-pbm-check="tax"]');
      if (tax) tax.hidden = true;
      var skipP = cl.querySelector('[data-pbm-checklist-skip]');
      if (skipP && skipP.parentElement) skipP.parentElement.hidden = true;
      var left = false;
      each(cl.querySelectorAll('[data-pbm-check]'), function (li) { if (!li.hidden) left = true; });
      if (!left) cl.hidden = true;
    }
    if (getCookie('pbm_tax_skip') === cid) apply();
    each(cl.querySelectorAll('[data-pbm-checklist-skip]'), function (a) {
      if (a.getAttribute('data-pbm-bound')) return;
      a.setAttribute('data-pbm-bound', '1');
      a.addEventListener('click', function (e) {
        e.preventDefault();
        setCookie('pbm_tax_skip', cid, 30 * DAY);
        apply();
      });
    });
  }

  function initCard(root) {
    var card = root.querySelector('[data-pbm-card]');
    if (card && card.getAttribute('data-pbm-ready')) return;
    if (card) card.setAttribute('data-pbm-ready', '1');
    else if (root.getAttribute('data-pbm-director-ready')) return;
    else root.setAttribute('data-pbm-director-ready', '1');

    var service = root.getAttribute('data-pbm-service');
    var cid = root.getAttribute('data-pbm-customer');
    var token = priv(root).card_token;
    var emailToken = priv(root).email_token || '';
    var authRow = card ? card.querySelector('[data-pbm-row="authorization"]') : null;
    var firstTaxOk = null;

    if (card) {
      /* Account type not tagged yet: the tile cookie from this browser decides visibility. */
      if (authRow && authRow.hidden && authRow.getAttribute('data-pbm-visibility') === 'type-unknown') {
        /* fix11: the account's signup:<tile> tag first (any device), then this browser's tile cookie. */
        var t = root.getAttribute('data-pbm-signup') || getCookie('pbm_type');
        /* fix12i (GL-F-38, LJ F-24): a government sign-up gets the organization card's Rx path (Upload Prescriber License,
           Upload Signed Authorization Form, the download, Send to My Doctor or Medical Director) like K8/L2, next to its tax
           step, so this browser's tile no longer opens the separate authorization row for it (that hid the Rx block below). */
        /* fix12i-c / fix12i-d (F-24, Cora): no sign-up tile opens the row any more (EMS in fix12i-c, provider in fix12i-d);
           every org sign-up keeps the card's Rx path, as the Liquid does for tagged accounts. The row stays for an
           authorization already sent or on file, or an Rx line (Liquid). t is kept for the rules below. */
        void t;
      }
      /* fix10 (F-t-growth-03): same rule for the optional tax line and the checklist (with Skip for Now). The store
         tags the account type later, so until then the tile cookie from this browser decides. */
      if (root.getAttribute('data-pbm-type') === 'unknown') {
        /* fix12i-f (GL-F-41): the type this customer saved on the organization form (this browser) comes before the sign-up
           tile: an account that signed up as Dental and then saved EMS gets the EMS tax step, as a fresh EMS sign-up does. */
        var tt = savedOrgType(root) || root.getAttribute('data-pbm-signup') || getCookie('pbm_type');
        var taxOk = taxForType(root, tt);
        firstTaxOk = taxOk;
        var authOk = !!(authRow && !authRow.hidden && authRow.getAttribute('data-pbm-visibility') === 'type-unknown');
        var anyItem = false;
        each(card.querySelectorAll('[data-pbm-maybe]'), function (n) {
          var w = n.getAttribute('data-pbm-maybe');
          if ((w === 'tax' && taxOk) || (w === 'auth' && authOk)) { n.hidden = false; if (n.tagName === 'LI') anyItem = true; }
        });
        var cl = card.querySelector('[data-pbm-checklist-maybe]');
        if (cl && anyItem) cl.hidden = false;
      }
      applyStoredTier(root, card);
      /* fix12g (F-53): Skip for Now in the checklist dismisses the optional tax item and stays on this page. */
      initChecklistSkip(root, card);
      /* fix12f (F-24): the card's Rx block stands in for the authorization row; one director form at a time. */
      var orgRx = card.querySelector('[data-pbm-org-rx]');
      if (orgRx && authRow && !authRow.hidden) orgRx.hidden = true;
    }

    bindDirectorForms(root, card, service, cid, emailToken, authRow);
    initRxStatus(root);
    if (!card) {
      /* OPP-12 loop 10/21: a page with an Rx block and no card (the personal screens) asks the card route once for its status. */
      var rxUrl = root.querySelector('[data-pbm-rx-status]') ? serviceUrl(service, 'card', { cid: cid, t: token }) : '';
      if (rxUrl && token) {
        getService(rxUrl, 12000).then(function (res) {
          if (res && res.ok !== false) rxStatus(root, res.card || res);
        }).catch(noop);
      }
      return;
    }

    /* [Resend]: open the same form with the last director filled in. */
    each(card.querySelectorAll('[data-pbm-resend]'), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var wrap = card.querySelector('[data-pbm-director]');
        if (!wrap) return;
        wrap.setAttribute('data-pbm-open', '1');
        show(wrap, true);
        var last = PBM.lastDirector || {};
        var nameEl = wrap.querySelector('[name="director_name"]');
        if (nameEl && !nameEl.value) nameEl.value = directorDisplay(last.name || btn.getAttribute('data-pbm-director-name') || '');
        var map = { director_email: last.email, state: last.state, licence_type: last.type, licence_number: last.number };
        Object.keys(map).forEach(function (k) {
          var el = wrap.querySelector('[name="' + k + '"]');
          if (el && !el.value && map[k]) el.value = map[k];
        });
        if (nameEl) try { nameEl.focus(); } catch (e2) { /* ignore */ }
      });
    });

    /* [Request our W-9] (route w9). */
    each(card.querySelectorAll('[data-pbm-w9]'), function (btn) {
      btn.addEventListener('click', function (e) {
        e.preventDefault();
        var url = serviceUrl(service, 'w9');
        var done = card.querySelector('[data-pbm-w9-done]');
        var err = card.querySelector('[data-pbm-w9-error]');
        if (!url || !token) { show(err, true); return; }
        show(err, false);
        busy(btn, true);
        postWithFreshToken(root, url, function (p) {
          return { customer_id: cid, token: p.card_token, email: root.getAttribute('data-pbm-email') || '', email_token: p.email_token || '' };
        }, { timeout: 10000 })
          .then(function (res) {
            if (!res || res.ok === false) throw new Error('failed');
            show(done, true);
            try { done.focus(); } catch (e2) { /* ignore */ }
            btn.hidden = true;
          })
          .catch(function () { busy(btn, false); show(err, true); });
      });
    });

    /* Live overlay from the service's card route. */
    var cardUrl = serviceUrl(service, 'card', { cid: cid, t: token });
    /* fix12i-f (GL-F-39): the section's early script held the headline (visibility only) while the tier is pending and this
       tab has no tier yet; it is shown once the card route has answered (or failed), so the first headline seen is the final one. */
    function releaseHead() {
      each(card.querySelectorAll('[data-pbm-tier-hold]'), function (n) { n.style.visibility = ''; n.removeAttribute('data-pbm-tier-hold'); });
      /* fix12i-g (GL-F-39): the skeleton line the early script put in the held space goes in the same task as the headline is shown. */
      each(card.querySelectorAll('[data-pbm-skel]'), function (n) { if (n.parentNode) n.parentNode.removeChild(n); });
      each(card.querySelectorAll('[data-pbm-skel-pos]'), function (n) { n.style.position = ''; n.removeAttribute('data-pbm-skel-pos'); });
    }
    /* fix12i-g (GL-F-41): the service's customer row keeps the type saved on the organization form (any device); the card route
       returns it as org_type. It comes first, then this browser's saved type, then the sign-up tile. Only the tax step follows it,
       only while the store has not tagged the account, and only when it changes the first decision (Skip for Now is kept). */
    function applyOrgType(c) {
      var ot = c && c.org_type;
      if (!ot || orgTypes().indexOf(ot) < 0 || root.getAttribute('data-pbm-type') !== 'unknown' || firstTaxOk === null) return;
      if (cid && savedOrgType(root) !== ot) setCookie('pbm_org_type', cid + ':' + ot, 30 * DAY);
      var ok = taxForType(root, ot);
      if (ok === firstTaxOk) return;
      firstTaxOk = ok;
      var skipped = getCookie('pbm_tax_skip') === (cid || '1');
      each(card.querySelectorAll('[data-pbm-maybe="tax"]'), function (n) { n.hidden = !ok || (n.tagName === 'LI' && skipped); });
      var cl = card.querySelector('[data-pbm-checklist-maybe]');
      if (cl) {
        var left = false;
        each(cl.querySelectorAll('[data-pbm-check]'), function (li) { if (!li.hidden) left = true; });
        cl.hidden = !left;
      }
    }
    if (root.getAttribute('data-pbm-type') === 'unknown') showTypeName(card, savedOrgType(root));
    if (!cardUrl || !token) { releaseHead(); return; }
    /* fix12i-g (F-45): right after the organization form was saved in this tab, a card answer with no tier yet is not final:
       the headline stays held (skeleton) and the card route is asked again every 1.5 s, until it names the tier or 20 s after
       the save. Then, or if it fails, the same rule as any load applies. */
    var savedAt = orgSavedAt(cid);
    /* fix12i-j (F-46): an untagged account's authorization row (and the Rx block it replaces) is only set by this lookup, so one
       slow or failed answer left the row hidden for the whole visit. The first lookup waits up to 12 s; a network error, a
       timeout or "busy" is retried after about 2 s, then about 6 s; bad_token re-reads this section's tokens once and asks again
       with the new token. At most 3 lookups for these retries (the F-45 post-save polling keeps its own 20 s window). A retry
       releases the held headline first, as a failed lookup always did. */
    var CARD_MAX = 3, CARD_WAITS = [2000, 6000], cardTries = 0, cardRetries = 0, cardRefreshed = false;
    function cardLookup() {
      cardTries++;
      return getService(serviceUrl(service, 'card', { cid: cid, t: priv(root).card_token || token }), 12000);
    }
    function cardRetry(why) {
      if (cardTries >= CARD_MAX || cardRetries >= CARD_WAITS.length) return null;
      releaseHead();
      var wait = CARD_WAITS[cardRetries++];
      return new Promise(function (r) { setTimeout(r, wait); }).then(ask);
    }
    function ask() {
      return cardLookup().then(function (res) {
        if (res && res.ok === false && res.error === 'bad_token' && !cardRefreshed && cardTries < CARD_MAX) {
          cardRefreshed = true;
          releaseHead();
          return refreshTokens(root).then(ask, function () { return res; });
        }
        if (res && res.ok === false && res.error === 'busy') return cardRetry('busy') || res;
        if (savedAt && res && res.ok !== false && !((res.card || res).validation_tier) && Date.now() - savedAt < POST_SAVE_MS - 1500) {
          return new Promise(function (r) { setTimeout(r, 1500); }).then(ask);
        }
        return res;
      }, function (err) {
        var again = cardRetry('network');
        if (again) return again;
        throw err;
      });
    }
    ask().then(function (res) {
      if (!res || res.ok === false) return;
      var c = res.card || res;
      applyLive(card, c);
      rxStatus(root, c);
      applyOrgType(c);
      if (root.getAttribute('data-pbm-type') === 'unknown' && c && c.org_type) showTypeName(card, c.org_type);
      /* fix12i-k (F-51, S1): a verified store record wins over a lagging or empty service answer (see finalTier). */
      var liveTier = c && c.validation_tier ? finalTier(card, c.validation_tier) : '';
      if (c && c.validation_tier && !liveTier) {
        /* The service's tier lost to the store's verified record: nothing to swap, and nothing kept for this tab. */
      } else if (liveTier) {
        /* fix12i (GL-F-39): keep the live tier for this tab, so a Back or reload paints the right panel first (pbm-register.liquid early tier script). */
        if (liveTier === 'recognized' || liveTier === 'personal' || liveTier === 'flagged') {
          rememberTier(root, { tier: liveTier, org_type: c.org_type, rule_version: c.rule_version });
        }
        applyStoredTier(root, card, liveTier);
      } else if (root.getAttribute('data-pbm-salesfirst') === 'on') {
        /* fix12i-f (GL-F-39): no tier yet; remember that for this tab, so the next load paints without holding. */
        try { if (!sessionStorage.getItem('pbm_tier_' + cid)) sessionStorage.setItem('pbm_tier_' + cid, JSON.stringify({ tier: 'pending' })); } catch (eT) { /* ignore */ }
      }
      /* Only for a visible card: a personal account behind its one-liner is outside the system. */
      if (!card.hidden && card.getAttribute('data-pbm-needs-record') === '1' && (c.found === false || c.organization === null || c.organization === undefined)) {
        cardBeacon(root);
      }
    }).catch(noop).then(function () {
      try { sessionStorage.removeItem(ORG_SAVED + cid); } catch (eS2) { /* ignore */ }
      releaseHead();
    });
  }

  /* An account that already had a Company before go-live never passes Screen 2. When neither
     the store (pbm.status) nor the service has a record, send the org beacon once from the
     default address so the account really enters verification (contract changes, package B). */
  function cardBeacon(root) {
    var cid = root.getAttribute('data-pbm-customer');
    var flag = 'pbm_card_beacon_' + cid;
    try { if (sessionStorage.getItem(flag)) return; sessionStorage.setItem(flag, '1'); } catch (e) { /* send anyway */ }
    var p = priv(root);
    var url = serviceUrl(root.getAttribute('data-pbm-service'), 'org');
    if (!url || !p.org_token) return;
    var d = root.dataset;
    var t = d.pbmType;
    if (orgTypes().indexOf(t) === -1) t = getCookie('pbm_type');
    if (orgTypes().indexOf(t) === -1) t = '';
    var cardPayload = {
      customer_id: cid,
      email: d.pbmEmail || '',
      first_name: d.pbmFirst || '',
      last_name: d.pbmLast || '',
      type: t,
      company: d.pbmCompany || '',
      street: d.pbmStreet || '',
      suite: d.pbmSuite || '',
      city: d.pbmCity || '',
      state: d.pbmStateCode || '',
      zip: d.pbmZip || '',
      phone: d.pbmPhone || '',
      signup: 'password',
      return: '',
      token: p.org_token,
      email_token: p.email_token || ''
    };
    salesfirstPayload(root, cardPayload);
    postService(url, cardPayload, { keepalive: true, timeout: root.getAttribute('data-pbm-salesfirst') === 'on' ? 8000 : 5000 }).then(function (res) {
      rememberTier(root, res);
    }).catch(noop);
  }

  /* fix12i-k (F-51, S1): the store's verified organization (pbm.status verified or linked, data-pbm-store-verified) wins over a
     lagging or empty service answer, and the headline never downgrades after load. With a final store tier (recognized,
     personal; skip keeps its legacy headline) the store's tier stands whatever the service says; with no store tier yet a
     verified account never gets the flagged headline (the legacy "ready" headline stays). '' = no swap. */
  function finalTier(card, tier) {
    if (!card || card.getAttribute('data-pbm-store-verified') !== '1') return tier;
    var store = card.getAttribute('data-pbm-tier') || '';
    if (store === 'recognized' || store === 'personal' || store === 'skip') return store === tier ? tier : '';
    return tier === 'flagged' ? '' : tier;
  }

  /* OPP-12: show S1, S2 or S5 once classify (or the desk metafield) names the tier. */
  function applyStoredTier(root, card, liveTier) {
    if (!root || !card || root.getAttribute('data-pbm-salesfirst') !== 'on') return;
    var tier = liveTier || card.getAttribute('data-pbm-tier') || '';
    if (tier === 'skip') return;
    if (!tier || tier === 'pending') {
      try {
        var saved = JSON.parse(sessionStorage.getItem('pbm_tier_' + root.getAttribute('data-pbm-customer')) || 'null');
        if (saved && saved.tier) tier = saved.tier;
      } catch (e) { /* server copy stands */ }
      /* fix12i-k (F-51): a tier this tab learned earlier is subject to the same store-verified rule. */
      if (tier) tier = finalTier(card, tier);
    }
    if (tier !== 'recognized' && tier !== 'personal' && tier !== 'flagged') return;
    var panel = headShow(card, tier);
    /* fix12i-h: a panel that came in from its slot after boot gets its fold links bound, in the section's scope so #pbm-director is
       found (links bound at boot are skipped; the page-hash open is not repeated). */
    if (panel) initFolds(root, true);
  }

  function headKey(n) { return n.hasAttribute('data-pbm-sf-legacy') ? 'legacy' : (n.getAttribute('data-pbm-sf') || ''); }
  function headShow(card, key) {
    /* fix12i-h: the block for key goes into the DOM (from its <template> slot if parked); every other headline block is parked in
       its slot, so the DOM holds one headline. Hold and skeleton state leave with a parked block. */
    var sel = '[data-pbm-sf-legacy],[data-pbm-sf-panel]', live = card.querySelectorAll(sel), slots = card.querySelectorAll('template[data-pbm-head-slot]'), want = null, i, n, s, k, x;
    for (i = 0; i < live.length; i++) if (headKey(live[i]) === key) want = live[i];
    for (i = 0; !want && i < slots.length; i++) {
      if (slots[i].getAttribute('data-pbm-head-slot') !== key || !(n = slots[i].content.querySelector(sel))) continue;
      slots[i].parentNode.insertBefore(n, slots[i]); want = n;
    }
    if (!want) return null;
    want.hidden = false;
    for (i = 0; i < live.length; i++) {
      n = live[i]; if (n === want) continue; k = headKey(n); s = null;
      for (x = 0; x < slots.length; x++) if (slots[x].getAttribute('data-pbm-head-slot') === k) s = slots[x];
      if (!s) { s = document.createElement('template'); s.setAttribute('data-pbm-head-slot', k); n.parentNode.insertBefore(s, n); }
      n.style.visibility = ''; n.removeAttribute('data-pbm-tier-hold');
      if (n.getAttribute('data-pbm-skel-pos')) { n.style.position = ''; n.removeAttribute('data-pbm-skel-pos'); }
      var sk = n.querySelectorAll('[data-pbm-skel]'); for (x = 0; x < sk.length; x++) sk[x].parentNode.removeChild(sk[x]);
      s.content.appendChild(n);
    }
    return want;
  }

  function applyLive(card, c) {
    function durable(row) {
      var el = card.querySelector('[data-pbm-row="' + row + '"]');
      return el ? el.getAttribute('data-pbm-durable') || '' : '';
    }

    /* Organization. merge() keeps a durable verified, linked or declined over a lagging service
       row; "verified" is only lagging next to a durable "linked" (a linked buyer is verified). */
    var liveOrg = c.organization || '';
    if (liveOrg === 'verified' && c.linked) liveOrg = 'linked';
    var durOrg = durable('organization');
    var orgState = (durOrg === 'verified' && liveOrg === 'verified') ? 'verified' : merge('organization', durOrg, liveOrg);
    if (durOrg === 'verified' && liveOrg === 'linked') orgState = 'linked';
    setRow(card, 'organization', orgState);

    /* Medical director authorization. "exempt" (verified hospital, hospital grandfather rows)
       hides the line; "on_file" with no expiry anywhere reads "On file ✓" (LJ 9/27). */
    var a = c.authorization || {};
    var authRow = card.querySelector('[data-pbm-row="authorization"]');
    var durAuth = durable('authorization');
    var durExp = (authRow && authRow.getAttribute('data-pbm-expires')) || '';
    var authState = merge('authorization', durAuth, a.status || '');
    /* The store's record (written by the desk) owns the expiry of an on-file authorization; the
       service may still hold an older date after a renewal. */
    var knownExpiry = (durAuth === 'on_file' || durAuth === 'on_file_nodate') ? durExp : (a.expires || durExp);
    if (authState === 'on_file' && !knownExpiry) authState = 'on_file_nodate';
    if (authState === 'on_file_nodate' && knownExpiry) authState = 'on_file';
    /* Nothing writes "expired" (the service keeps on_file): a past expiry means expired. */
    if (authState === 'on_file' && isPast(knownExpiry)) authState = 'expired';
    var authVars = {};
    if (a.director_name) authVars.director_name = directorDisplay(a.director_name);
    if (a.sent_at) authVars.sent_at = formatDate(a.sent_at);
    if (knownExpiry) authVars.expires = formatDate(knownExpiry);
    /* "Dr" only for MD, DO, DDS, DMD, DPM and unknown, like the service's emails (Cora 9/27; DPM LJ 10/19). */
    if (a.licence_type) {
      var noDr = ['NP', 'CNM', 'LM', 'PA', 'RPH', 'OTHER'].indexOf(String(a.licence_type).toUpperCase()) > -1;
      each(card.querySelectorAll('[data-pbm-dr]'), function (n) { n.hidden = noDr; });
    }
    if (authState === 'exempt') {
      if (authRow) { authRow.hidden = true; authRow.setAttribute('data-pbm-visibility', 'never'); }
    } else {
      setRow(card, 'authorization', authState, authVars);
      /* fix2 (F-56): a card that shows the V2 Rx block keeps it for sent, signed and on-file answers too (its status lines,
         rxStatus, show them); the old row only opens where there is no V2 block. */
      var orgRxL = card.querySelector('[data-pbm-org-rx]');
      if (authRow && authRow.getAttribute('data-pbm-visibility') !== 'never' && a.status && a.status !== 'none' && !(orgRxL && !orgRxL.hidden)) {
        authRow.hidden = false;
      }
    }
    if (a.director_name) {
      each(card.querySelectorAll('[data-pbm-resend]'), function (b) { b.setAttribute('data-pbm-director-name', a.director_name); });
    }

    /* Tax exemption: a certificate that needs a different form reads like "not on file". */
    var tx = (c.tax && c.tax.status) || '';
    if (tx === 'needs_form') tx = 'none';
    setRow(card, 'tax', merge('tax', durable('tax'), tx));

    if (c.w9_sent_at) {
      var done = card.querySelector('[data-pbm-w9-done]');
      var btn = card.querySelector('[data-pbm-w9]');
      if (done && btn && btn.hidden) show(done, true);
    }

    /* Declined (live, before the store's record says so): only the Organization line, like the
       durable declined card. */
    if (orgState === 'declined') {
      each(card.querySelectorAll('[data-pbm-row]'), function (r) {
        if (r.getAttribute('data-pbm-row') === 'organization') return;
        r.hidden = true;
        r.setAttribute('data-pbm-visibility', 'never');
      });
    }
  }

  /* ------------------------------------------------------------------ the cart */

  /* [Buy the other N now]: remove the gated lines with /cart/change.js by line index,
     highest index first so the lower ones do not move. The indexes are re-read from /cart.js
     by line key first, and checkout opens only if exactly the expected lines remain. */
  function initCart() {
    if (PBM.cartReady) return;
    PBM.cartReady = true;
    document.addEventListener('click', function (e) {
      var btn = e.target && e.target.closest ? e.target.closest('[data-pbm-buy-others]') : null;
      if (!btn) return;
      e.preventDefault();
      var keys = (btn.getAttribute('data-pbm-remove-keys') || '').split(',').filter(Boolean);
      if (!keys.length) return;
      busy(btn, true);
      var noteEl = document.getElementById('note');
      /* One line at a time, highest index first, re-reading the cart after every change: a
         discount that splits or merges lines cannot shift an index onto the wrong item. */
      function removeNext(cart, guard) {
        var line = 0;
        (cart.items || []).forEach(function (item, i) {
          if (keys.indexOf(item.key) > -1) line = i + 1;
        });
        if (!line || guard > 20) return Promise.resolve(cart);
        return fetch('/cart/change.js', {
          method: 'POST',
          credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: line, quantity: 0 })
        }).then(function (r) {
          if (!r.ok) throw new Error('change_' + r.status);
          return r.json();
        }).then(function (next) { return removeNext(next, guard + 1); });
      }
      fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
        .then(function (r) { return r.json(); })
        .then(function (cart) { return removeNext(cart, 0); })
        .then(function () {
          if (noteEl && noteEl.value !== noteEl.defaultValue) {
            return fetch('/cart/update.js', {
              method: 'POST',
              credentials: 'same-origin',
              headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
              body: JSON.stringify({ note: noteEl.value })
            }).catch(noop);
          }
        })
        .then(function () {
          if (btn.getAttribute('data-pbm-after') !== 'checkout') { location.href = '/cart'; return; }
          /* Straight to checkout only when the cart now holds exactly the lines shown as "the other N";
             anything added in another tab since the page rendered goes back through the cart page. */
          var keep = (btn.getAttribute('data-pbm-keep-keys') || '').split(',').filter(Boolean);
          return fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store', headers: { Accept: 'application/json' } })
            .then(function (r) { return r.json(); })
            .then(function (cart) {
              var items = cart.items || [];
              var exact = items.length === keep.length && items.every(function (it) { return keep.indexOf(it.key) > -1; });
              location.href = exact ? '/checkout' : '/cart';
            });
        })
        .catch(function () { location.href = '/cart'; });
    });
  }

  /* -------------------------------------------------------------- login page */

  function initLoginNotice(root) {
    /* OPP-01 #7/#10: one sign-in form with the email already filled in (from Screen 1, or kept after a failed login). */
    var loginEmail = document.getElementById('customer_email');
    if (loginEmail) {
      if (!loginEmail.value) loginEmail.value = recalledEmail();
      if (!loginEmail.getAttribute('autocomplete')) loginEmail.setAttribute('autocomplete', 'email');
      var loginForm = loginEmail.form;
      if (loginForm) loginForm.addEventListener('submit', function () { rememberEmail(loginEmail.value); });
    }
    var recEmail = document.getElementById('recover-email');
    if (recEmail && !recEmail.value) recEmail.value = recalledEmail();
    /* fix12f (F-t-assess-30): a reset request is never a sign-up answer; it clears the marker. */
    var recForm = recEmail ? recEmail.form : null;
    if (recForm) recForm.addEventListener('submit', function () { delCookie('pbm_dup'); });
    var dup = root.querySelector('[data-pbm-dup]');
    /* fix12f (F-t-assess-30): the marker is read once and cleared on EVERY sign-in page load. The notice shows only on
       the real post-signup redirect: a fresh marker (set by the create form's own submit), this page reached straight
       from the sign-up page (same-origin referrer, a plain navigation, not a reload or back/forward), and not the
       reset flow (#recover in the URL, or the reset panel open after a reset request). */
    var ts = parseInt(getCookie('pbm_dup'), 10);
    delCookie('pbm_dup');
    if (!dup) return;
    var fresh = ts && Date.now() - ts >= 0 && Date.now() - ts < DUP_FRESH_MS;
    var fromSignup = false;
    try {
      var ref = document.referrer ? new URL(document.referrer) : null;
      fromSignup = !!ref && ref.origin === location.origin && ['/pages/register', '/account/register', '/account'].indexOf(ref.pathname.replace(/\/$/, '')) > -1;
    } catch (e9) { fromSignup = false; }
    var navType = '';
    try { var nav = performance.getEntriesByType('navigation')[0]; navType = nav ? nav.type : ''; } catch (e10) { /* ignore */ }
    var recPanel = document.getElementById('recover');
    var resetFlow = location.hash === '#recover' || location.hash === '#recover-password' || !!(recPanel && recPanel.style.display === 'block');
    if (fresh && fromSignup && navType !== 'reload' && navType !== 'back_forward' && !resetFlow) show(dup, true);
    else show(dup, false);
    each(dup.querySelectorAll('[data-pbm-focus-login]'), function (a) {
      a.addEventListener('click', function (e) {
        var email = document.getElementById('customer_email');
        if (!email) return;
        e.preventDefault();
        var login = document.getElementById('login');
        var recover = document.getElementById('recover');
        /* The theme shows #recover through a CSS :target rule; move the target to #login. */
        if (location.hash !== '#login') location.hash = 'login';
        if (recover) recover.style.display = 'none';
        if (login) login.style.display = '';
        var pw = document.getElementById('customer_password');
        try { (email.value && pw ? pw : email).focus(); } catch (e2) { /* ignore */ }
      });
    });
    each(dup.querySelectorAll('[data-pbm-show-recover]'), function (a) {
      a.addEventListener('click', function (e) {
        var login = document.getElementById('login');
        var recover = document.getElementById('recover');
        if (!login || !recover) return;
        e.preventDefault();
        if (location.hash !== '#recover') location.hash = 'recover';
        login.style.display = 'none';
        recover.style.display = 'block';
        var em = document.getElementById('recover-email');
        if (em) try { em.focus(); } catch (e2) { /* ignore */ }
      });
    });
  }

  /* -------------------------------------------------------------------- boot */

  /* OPP-01 #18/#19: the checklist links and #pbm-director / #pbm-tax open the folded card section. */
  function initFolds(scope, noHash) {
    function openFor(hash) {
      if (!hash) return;
      var target = null;
      try { target = (scope || document).querySelector(hash); } catch (e) { return; }
      if (!target) return;
      var fold = target.querySelector('details.pbm-fold');
      if (fold && !fold.hidden) fold.open = true;
      try { target.focus({ preventScroll: false }); } catch (e) { /* ignore */ }
    }
    each((scope || document).querySelectorAll('[data-pbm-open-fold]'), function (a) {
      if (a.__pbmFold) return;
      a.__pbmFold = 1;
      a.addEventListener('click', function (e) {
        var h = a.getAttribute('href');
        if (!h || h.charAt(0) !== '#') return;
        e.preventDefault();
        openFor(h);
      });
    });
    if (!noHash && (location.hash === '#pbm-director' || location.hash === '#pbm-tax')) openFor(location.hash);
  }

  function boot(scope) {
    var roots = (scope || document).querySelectorAll('[data-pbm-root]');
    /* fix12i-f (GL-F-42): an organization draft belongs to one customer; another customer (or none) signed in drops it. */
    if (roots.length) dropOrgDrafts(roots[0].getAttribute('data-pbm-customer') || '');
    if (!PBM.logoutBound) {
      PBM.logoutBound = true;
      document.addEventListener('click', function (e) {
        var a = e.target && e.target.closest ? e.target.closest('a[href*="/account/logout"]') : null;
        if (a) dropOrgDrafts('');
      }, true);
    }
    each(roots, function (root) {
      var mode = root.getAttribute('data-pbm-mode');
      if (mode === 'login') { initLoginNotice(root); return; }
      /* fix12: signed in (any state but the create screen), the signup draft has done its job. */
      var st = root.getAttribute('data-pbm-state');
      if (st && st !== 'create' && st !== 'login') {
        try { sessionStorage.removeItem(DRAFT_KEY); } catch (e8) { /* ignore */ }
        /* fix12f (F-t-assess-30): signed in, so the sign-up went through; no duplicate-email marker may outlive it. */
        delCookie('pbm_dup');
      }
      /* fix12g part 3 (F-30-GL): one part failing never leaves the card's buttons unbound. */
      [initScreen1, initScreen2, initPersonal, initCard, initFolds].forEach(function (fn) {
        try { fn(root); } catch (e9) { try { console.error('PBM', fn.name, e9); } catch (e10) { /* ignore */ } }
      });
    });
    try { initCart(); } catch (e11) { /* ignore */ } /* one delegated listener; the cart footer is swapped after quantity changes */
    /* fix12g part 3 (F-30-GL): the section's parse-time script held any button press made before this ran; replay it now. */
    PBM.booted = true;
    var held = window.__pbmEarlyQ || [];
    window.__pbmEarlyQ = [];
    each(held, function (el) {
      if (!el || !el.isConnected) return;
      if (el.tagName === 'FORM') {
        if (el.requestSubmit) el.requestSubmit(); else el.dispatchEvent(new Event('submit', { cancelable: true }));
      } else el.click();
    });
  }

  PBM.boot = boot;

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { boot(); });
  else boot();

  /* Theme editor: sections are re-rendered in place. */
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  /* The cart page swaps .cart__footer after quantity changes; the click handler is delegated. */
})();
